import type { QueryBuilder } from "@cgarciagarcia/react-query-builder";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getProfile,
  getProfileDetail,
  getProfiles,
  getProfileSurveys,
  updateProfileDetail,
  updateProfileSummary,
} from "@/api";
import type { Profile } from "@/types/profile";
import { errorToast, useToastStore } from "@/ui";

export const useProfile = () => {
  const queryClient = useQueryClient();

  const { pushToast } = useToastStore();

  const useGetProfiles = ({ queryBuilder }: { queryBuilder?: QueryBuilder }) => {
    return useQuery({
      queryFn: () => {
        return getProfiles({
          queryBuilder,
        });
      },
      queryKey: ["getProfiles", queryBuilder?.toArray()],
    });
  };

  const useGetProfile = (id: Profile["id"]) => {
    return useQuery({
      queryKey: ["getProfile", id],
      queryFn: () => {
        return getProfile(id);
      },
      enabled: !!id,
    });
  };

  const useGetProfileDetail = (id: Profile["id"]) => {
    return useQuery({
      queryKey: ["getProfileDetail", id],
      queryFn: () => {
        return getProfileDetail(id);
      },
      enabled: !!id,
    });
  };

  const useUpdateProfileSummary = () => {
    return useMutation({
      mutationFn: updateProfileSummary,
      onSuccess: async ({ id }) => {
        await queryClient.invalidateQueries({ queryKey: ["getProfile", id] });

        void pushToast({ type: "success", message: "Profile updated successfully" });
      },
      onError: errorToast,
    });
  };

  const useUpdateProfileDetail = () => {
    return useMutation({
      mutationFn: updateProfileDetail,
      onSuccess: async (_date, variables) => {
        const { id } = variables;
        await queryClient.invalidateQueries({
          queryKey: ["getProfileDetail", id],
        });
        void pushToast({
          type: "success",
          message: "Profile details updated successfully",
        });
      },
      onError: errorToast,
    });
  };

  const useGetProfileSurveys = (id: Profile["id"]) => {
    return useQuery({
      queryKey: ["getProfileSurveys", id],
      queryFn: () => {
        return getProfileSurveys(id);
      },
      enabled: !!id,
    });
  };

  return {
    getProfiles: useGetProfiles,
    getProfile: useGetProfile,
    getProfileDetail: useGetProfileDetail,
    updateProfileSummary: useUpdateProfileSummary,
    updateProfileDetail: useUpdateProfileDetail,
    getProfileSurveys: useGetProfileSurveys,
  };
};
